import { useState, useEffect, useRef } from "preact/hooks";
import type { WidgetConfig } from "./types";
import { EditionProvider, useEdition } from "./hooks/useEdition";
import { useTranslations } from "./hooks/useTranslations";
import { useAppPrompt } from "./hooks/useAppPrompt";
import { parseSubRoute, joinPath } from "./lib/router";
import { ArtistsPage } from "./pages/ArtistsPage";
import { ArtistDetailPage } from "./pages/ArtistDetailPage";
import { SchedulesPage } from "./pages/SchedulesPage";
import { EventDetailPage } from "./pages/EventDetailPage";
import { EventsPage } from "./pages/EventsPage";
import { PlacesPage } from "./pages/PlacesPage";
import { PlaceDetailPage } from "./pages/PlaceDetailPage";
import { AppPromptModal } from "./components/ui/AppPromptModal";
import { ErrorBoundary } from "./components/ui/ErrorBoundary";
import { PoweredByBadge } from "./components/ui/PoweredByBadge";
import { CSSCustomizer } from "./components/ui/css-customizer";

type AppProps = {
  config: WidgetConfig;
  containerRef: preact.RefObject<HTMLElement>;
};

export function App({ config, containerRef }: AppProps) {
  return (
    <ErrorBoundary>
      <EditionProvider config={config} containerRef={containerRef}>
        <AppContent initialSubRoute={config.subRoute} />
      </EditionProvider>
      {config.isAdmin && <CSSCustomizer />}
    </ErrorBoundary>
  );
}

type AppContentProps = {
  initialSubRoute: string;
};

function AppContent({ initialSubRoute }: AppContentProps) {
  const { mode, basePath, loading: editionLoading, edition, locale, routingMode } = useEdition();
  const t = useTranslations();
  const dynamicLink = edition?.festival?.dynamic_link;
  const { showPrompt, dismissPrompt, openApp } = useAppPrompt(dynamicLink);
  const isHash = routingMode === "hash";

  const [currentPath, setCurrentPath] = useState(() => {
    if (isHash) {
      const hash = window.location.hash.replace(/^#\/?/, "");
      return hash ? `/${hash}` : "/";
    }
    if (initialSubRoute) {
      return joinPath(basePath, initialSubRoute);
    }
    return basePath;
  });

  // In-app navigation history. Drives the Back button WITHOUT emitting a
  // popstate: some host-page routers (e.g. WordPress's Interactivity API)
  // listen for popstate and turn the URL change into a full page reload.
  // Each entry remembers the item to scroll back to when the view reappears.
  const historyStackRef = useRef<Array<{ path: string; scrollTo?: string }>>([
    { path: currentPath },
  ]);

  // Reconcile the in-memory history with a location the browser navigated to on
  // its own (native back/forward). Pop on a single-step back; otherwise reset.
  const syncStackToLocation = (loc: string, scrollTo?: string) => {
    const stack = historyStackRef.current;
    if (stack.length > 1 && stack[stack.length - 2].path === loc) {
      stack.pop();
    } else if (stack[stack.length - 1].path !== loc) {
      stack.length = 0;
      stack.push({ path: loc, scrollTo });
    }
  };

  // Parse the current route
  const route = parseSubRoute(
    isHash
      ? currentPath.replace(/^\//, "")
      : currentPath.replace(basePath, "").replace(/^\//, "")
  );

  // Handle navigation
  const navigate = (path: string, elementId?: string) => {
    const stack = historyStackRef.current;
    // Remember which item to scroll back to on the view we're leaving.
    if (stack.length) stack[stack.length - 1].scrollTo = elementId;

    if (isHash) {
      // path is either an absolute path (path mode) or a hash fragment (#/...)
      const hashPath = path.startsWith("#") ? path : `#/${path.replace(/^\//, "")}`;
      const frag = hashPath.replace(/^#/, "");
      stack.push({ path: frag });
      try {
        window.location.hash = frag;
      } catch {
        // ignore
      }
      setCurrentPath(frag);
    } else {
      stack.push({ path });
      // Try to update browser URL (may fail in iframes/Elementor preview)
      try {
        // Stamp the view we're leaving so a browser back/forward popstate can
        // scroll back to the tapped item (replaceState emits no popstate, so
        // host routers ignore it).
        window.history.replaceState({ elementId }, "");
        // Update browser URL
        window.history.pushState({}, "", path);
      } catch {
        // Ignore - history API blocked in iframe context
      }
      setCurrentPath(path);
    }
    // Scroll to top of widget
    requestAnimationFrame(() => {
      document.querySelector(".festapp-widget")?.scrollIntoView({ behavior: "smooth" });
    });
  };

  // Replay our own in-memory history instead of calling history.back(). A
  // popstate from history.back() gets hijacked into a full reload by some host
  // routers; replaceState keeps us in-document and emits no popstate.
  const goBack = () => {
    const stack = historyStackRef.current;
    let target: { path: string; scrollTo?: string };
    if (stack.length > 1) {
      stack.pop();
      target = stack[stack.length - 1];
    } else {
      // Deep-linked straight to a detail page: no in-app history to pop, so go
      // up to the listing instead of leaving the page entirely.
      target = { path: isHash ? "/" : basePath || "/" };
      historyStackRef.current = [target];
    }

    if (isHash) {
      const frag = target.path.startsWith("/") ? target.path : `/${target.path}`;
      try {
        window.location.hash = frag;
      } catch {
        // ignore
      }
      setCurrentPath(frag);
      return;
    }

    try {
      window.history.replaceState({ scrollTo: target.scrollTo }, "", target.path);
    } catch {
      // Ignore - history API blocked in iframe context
    }
    setCurrentPath(target.path);

    const scrollTo = target.scrollTo;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = scrollTo && document.querySelector(`[data-item-id="${scrollTo}"]`);
        (el || document.querySelector(".festapp-widget"))?.scrollIntoView({
          behavior: "smooth",
          block: el ? "center" : "start",
        });
      });
    });
  };

  // Listen for browser back/forward
  useEffect(() => {
    if (isHash) {
      const handleHashChange = () => {
        const hash = window.location.hash.replace(/^#\/?/, "");
        const frag = hash ? `/${hash}` : "/";
        syncStackToLocation(frag);
        setCurrentPath(frag);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.querySelector(".festapp-widget")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          });
        });
      };
      window.addEventListener("hashchange", handleHashChange);
      return () => window.removeEventListener("hashchange", handleHashChange);
    }

    const handlePopState = (event: PopStateEvent) => {
      const loc = window.location.pathname;
      syncStackToLocation(loc, event.state?.elementId);
      setCurrentPath(loc);
      const elementId = event.state?.elementId;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = elementId && document.querySelector(`[data-item-id="${elementId}"]`);
          (el || document.querySelector(".festapp-widget"))?.scrollIntoView({
            behavior: "smooth",
            block: el ? "center" : "start"
          });
        });
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isHash]);

  if (editionLoading) {
    return (
      <div className="festapp-loading">
        <div className="festapp-spinner" />
      </div>
    );
  }

  if (!editionLoading && !edition) {
    return (
      <div className="festapp-error">
        <p>{t.failedToLoadEdition}</p>
      </div>
    );
  }

  const appPromptModal = showPrompt && dynamicLink && (
    <AppPromptModal
      dynamicLink={dynamicLink}
      onClose={dismissPrompt}
      onOpenApp={openApp}
      locale={locale}
    />
  );

  // Render based on current route
  if (route.type === "artist" && route.id) {
    return (
      <>
        {appPromptModal}
        <ArtistDetailPage
          artistId={route.id}
          onNavigate={navigate}
          onBack={goBack}
        />
        <PoweredByBadge />
      </>
    );
  }

  if (route.type === "event" && route.id) {
    return (
      <>
        {appPromptModal}
        <EventDetailPage
          eventId={route.id}
          onNavigate={navigate}
          onBack={goBack}
        />
        <PoweredByBadge />
      </>
    );
  }

  if (route.type === "place" && route.id) {
    return (
      <>
        {appPromptModal}
        <PlaceDetailPage
          placeId={route.id}
          onBack={goBack}
        />
        <PoweredByBadge />
      </>
    );
  }

  // Default: show list view based on mode
  if (mode === "schedules") {
    return (
      <>
        {appPromptModal}
        <SchedulesPage onNavigate={navigate} />
        <PoweredByBadge />
      </>
    );
  }

  if (mode === "events") {
    return (
      <>
        {appPromptModal}
        <EventsPage onNavigate={navigate} />
        <PoweredByBadge />
      </>
    );
  }

  if (mode === "places") {
    return (
      <>
        {appPromptModal}
        <PlacesPage onNavigate={navigate} />
        <PoweredByBadge />
      </>
    );
  }

  return (
    <>
      {appPromptModal}
      <ArtistsPage onNavigate={navigate} />
      <PoweredByBadge />
    </>
  );
}
